import angular, { IPromise, IScope } from 'angular'
import '@uirouter/angularjs'
import 'angular-animate'
import 'angular-messages'
import 'angular-resource'
import '@vend/angular-vs-repeat'
import 'moment'
import { ApiOutlet, ApiRegister, ApiRetailer, ApiUser } from '@vend/types'

import { EVENTS, REACT_TRAINING_MODE } from '../constants'
import { STATES } from '../../../lib/routes'
import { $q, $state, $transitions } from '../ng-compat/imports'
import { RegisterManager } from '../managers/register-manager'
import { TrainingModeManager } from '../managers/training-mode-manager'
import { DatabaseService, Store } from '../factories/database/database'
import { LoggedInUserChangeEvent, UserManager } from '../managers/user-manager'
import {
  RestrictedAccessChangeEvent,
  WebRegisterStatusManager,
} from '../managers/web-register-status-manager'
import { SyncManager } from '../managers/sync-manager'
import { SaleSyncManager } from '../managers/sale-sync-manager'
import { CashMovementsManager } from '../managers/cash-movements-manager'
import { RetailerSettingsManager } from '../managers/retailer-settings-manager'
import { UserSwitchingService } from '../components/users/user-switching/user-switching.service'
import { HotkeysService } from '../components/hotkeys/hotkeys.service'
import type { RedirectSigninOn401 } from '../http/redirect-signin-on-401'
import type { RegisterModals } from '../modals/register-modals'
import { RouteManagerService } from '../components/route-management/route-manager.service'
import { WebRegisterManager } from '../managers/web-register-manager'
import type { DuplicateWindowManager } from '../managers/duplicate-window-manager'
import { getFeatureFromCache } from '../util/feature-cache'

/**
 * =======================================
 * APP CONTROLLER
 * =======================================
 *
 * @module webregister.controllers.WebRegisterCtrl
 */
class WebRegisterCtrl {
  public cashManagementEnabled = false
  public hasNoProducts = false
  public initComplete = false
  public initialisingModal: $TSFixMe
  public outlet?: ApiOutlet
  public register?: ApiRegister
  public restrictedAccess = false
  public retailer: ApiRetailer
  public trainingModeEnabled = false
  public reactTrainingTour = false
  public user: ApiUser

  // Setting to true by default to prevent a flash of the offline topbar / text
  public webRegisterOnline = true

  constructor(
    private $scope: IScope,
    private webRegisterManager: WebRegisterManager,
    private registerModals: RegisterModals,
    public registerManager: RegisterManager,
    private duplicateWindowManager: DuplicateWindowManager,
    private trainingModeManager: TrainingModeManager,
    private webRegisterStatusManager: WebRegisterStatusManager,
    private routeManager: RouteManagerService,
    private retailerSettingsManager: RetailerSettingsManager,
    private cashMovementsManager: CashMovementsManager,
    private userManager: UserManager,
    private userSwitchingService: UserSwitchingService,
    private hotkeysService: HotkeysService,
    public saleSyncManager: SaleSyncManager,
    public syncManager: SyncManager,
    private redirectSigninOn401: RedirectSigninOn401,
    private productsStore: $TSFixMe,
    Database: DatabaseService
  ) {
    'ngInject'
    const rootScopeListeners: $TSFixMe[] = []
    this.user = this.userManager.getLoggedInUser()
    this.retailerSettingsManager.init()
    this.retailer = this.retailerSettingsManager.get()

    const initRegister = () => {
      return this.webRegisterManager.init().then(() => {
        this.trainingModeManager.init()
        this.hotkeysService.init()

        // Redirect to the main sell route on first boot after resetting data
        const coldBootResolver =
          !this.webRegisterManager.isWarmBoot && $state.includes(STATES.STATUS)
            ? (($state.go(STATES.SALE) as unknown) as IPromise<void>)
            : $q.resolve()
        coldBootResolver
          .then(() => {
            // Running the product code migration before the page has loaded. This is to prevent issues with scanning while
            // the migration is running.
            return this._checkAndHandleProductCodeMigration(
              Database.store('products')
            )
          })
          .then(() => {
            this.initComplete = true
            this.reactTrainingTour = getFeatureFromCache(REACT_TRAINING_MODE)
            // If the returnUrl address query param was specified, redirect to it now that the register has been initialized.
            const queryParams = new URLSearchParams(window.location.search)
            const returnUrlParam = queryParams.get('onInitRedirectUrl')

            if (returnUrlParam) {
              // create a fully qualified url from the param
              // if this is a relative URL, the return value of `new URL(... , document.baseURI)` will use the current `document.baseURI`
              // if this is an absolute URL, the return value of `new URL(... , document.baseURI)` will be using the same baseURI as `returnUrlParam` (it is not replaced by `document.baseURI`)
              const returnUrl = new URL(returnUrlParam, document.baseURI)

              // for security reasons we must ensure the address we redirect to stays within the LS application
              // so we will create a new url that definitely uses the current baseURI
              const safeUrl = new URL(
                returnUrl.pathname + returnUrl.search + returnUrl.hash, // take just the relative part of the `returnUrl`
                document.baseURI // replaces baseURI of `returnUrl` to be the current document's baseURI
              )
              window.location.replace(safeUrl)
            }
          })
      })
    }
    this.duplicateWindowManager
      .ensureSingleWindow()
      .then(initRegister, () => $state.go(STATES.DUPLICATE_WINDOW))
    this._updateTrainingMode()
    this._updateActiveOutletRegister()
    this._updateOnlineStatus()
    this.userManager.on('loggedInUserChange', this._loggedInUserChange, this)
    this.trainingModeManager.on(
      'trainingModeEnabledChange',
      this._updateTrainingMode,
      this
    )
    this.registerManager.on(
      'activeOutletChange',
      this._updateActiveOutletRegister,
      this
    )
    this.registerManager.on(
      'activeRegisterChange',
      this._updateActiveOutletRegister,
      this
    )
    this.webRegisterStatusManager.on(
      EVENTS.statusChange,
      this._updateOnlineStatus,
      this
    )
    this.webRegisterStatusManager.on(
      EVENTS.restrictedAccessChange,
      this._updateRestrictedAccessStatus,
      this
    )
    this.webRegisterManager.on(
      'registerSelectRequired',
      this._onRegisterSelectRequired,
      this
    )
    this.webRegisterManager.on(
      'registersUnavailable',
      this._onRegistersUnavailable,
      this
    )
    this.webRegisterManager.on(
      'registerSelected',
      this._onRegisterSelected,
      this
    )
    rootScopeListeners.push(
      $transitions.onSuccess({}, () => this._updateOnlineStatus())
    )
    this.syncManager.on('syncComplete', () => {
      this.productsStore.count().then((c: $TSFixMe) => {
        this.hasNoProducts = c <= 1 // there will always be 1 product due to the discount product
      })
    })
    this.$scope.$on('$destroy', () => {
      // Unbind listeners
      this.userManager.off('loggedInUserChange', this._loggedInUserChange)
      this.trainingModeManager.off(
        'trainingModeEnabledChange',
        this._updateTrainingMode
      )
      this.registerManager.off(
        'activeOutletChange',
        this._updateActiveOutletRegister
      )
      this.registerManager.off(
        'activeRegisterChange',
        this._updateActiveOutletRegister
      )
      this.webRegisterStatusManager.off(
        EVENTS.statusChange,
        this._updateOnlineStatus
      )
      this.webRegisterStatusManager.off(
        EVENTS.restrictedAccessChange,
        this._updateRestrictedAccessStatus
      )
      this.webRegisterManager.off(
        'registerSelectRequired',
        this._onRegisterSelectRequired
      )
      this.webRegisterManager.off(
        'registersUnavailable',
        this._onRegistersUnavailable
      )
      this.webRegisterManager.off('registerSelected', this._onRegisterSelected)
      rootScopeListeners.forEach(unbind => unbind())
    })
  }

  /**
   * Allows user to be switched from within navi's top navigation user side drawer.
   *
   * @method naviSwitchUser
   */
  public naviSwitchUser() {
    $q.when(this.userSwitchingService.selectUser()).catch(angular.noop)
  }

  private async _checkAndHandleProductCodeMigration(
    productEntityDb: Store<'products'>
  ) {
    const productCodeIndex = productEntityDb.index('byProductCode')
    const skuIndex = productEntityDb.index('bySku')

    const [productCodeIndexCount, skuIndexCount] = await Promise.all([
      productCodeIndex.count(),
      skuIndex.count(),
    ])

    if (skuIndexCount > 0 && productCodeIndexCount < skuIndexCount) {
      return productEntityDb.transformAll((value: any) => {
        return this.productsStore.transformProduct(value)
      })
    } else {
      return Promise.resolve()
    }
  }

  private _updateTrainingMode() {
    this.$scope.$applyAsync(() => {
      if (!this.userManager.hasAccessToSellScreen()) {
        this.trainingModeManager.disableTrainingMode()
      }
      this.trainingModeEnabled = this.trainingModeManager.trainingModeEnabled
    })
  }

  private _updateActiveOutletRegister() {
    const outlet = this.registerManager.getActiveOutlet()
    const register = this.registerManager.getActiveRegister()
    this.$scope.$applyAsync(() => {
      this.outlet = outlet
      this.register = register
      this.cashManagementEnabled =
        Boolean(register) && this.cashMovementsManager.isCashManagementEnabled()
    })
  }

  private _updateOnlineStatus() {
    const webRegisterOnline = this.webRegisterStatusManager.isOnline()
    this.$scope.$applyAsync(() => {
      this.webRegisterOnline = webRegisterOnline
    })
    if (
      !webRegisterOnline &&
      !this.routeManager.isStateAllowedInOfflineMode()
    ) {
      this.routeManager.openOfflineModal()
    } else {
      this.routeManager.closeOfflineModal()
    }
  }

  private _onRegisterSelectRequired() {
    this.registerModals.selectRegister().then((modal: $TSFixMe) => {
      this.initialisingModal = modal
    })
  }

  private _onRegistersUnavailable() {
    $state.go(STATES.REGISTERS_UNAVAILABLE)
  }

  private _onRegisterSelected() {
    if (!this.webRegisterManager.initialisationComplete) {
      // Still initialising, re-display the loading lightbox
      $q.resolve(this.initialisingModal && this.initialisingModal.closed)
    }
  }

  /**
   * Sets the user property to the logged in user sent through on the loggedInUserChange event object. When the user
   * changes, reload the navigation features.
   *
   * @private
   * @method _loggedInUserChange
   *
   * @param  {Event} event
   *         The event object.
   */
  private _loggedInUserChange(event: LoggedInUserChangeEvent) {
    this.$scope.$applyAsync(() => {
      this.user = event.loggedInUser
    })
  }

  /**
   * Updates the restricted access property as and when the restrictedAccessChange event is emitted.
   *
   * @private _updateRestrictedAccessStatus
   *
   * @param {Event} event
   *        The event object.
   */
  private _updateRestrictedAccessStatus({
    restrictedAccess,
  }: RestrictedAccessChangeEvent) {
    this.restrictedAccess = restrictedAccess
  }

  public isUnAuthenticated() {
    return this.redirectSigninOn401.hasEncountered401()
  }
}

angular
  .module('webregister.controllers')
  .controller('WebRegisterCtrl', WebRegisterCtrl)
